<template>
  <v-dialog v-model="model" max-width="900" scrollable :fullscreen="!device.isDesktop">
    <v-card elevation="10" :class="device.isDesktop ? 'rounded-xl' : 'rounded-0'">
      <v-card-title
        class="d-flex flex-column flex-md-row align-md-center justify-space-between pa-4"
      >
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-cog-outline</v-icon>
          <span class="text-h5 font-weight-bold">
            {{ $t("settings") }}
          </span>
        </div>
        <div class="d-flex align-center mt-3 mt-md-0">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="!device.isDesktop"
                icon
                color="primary"
                dark
                v-bind="attrs"
                v-on="on"
                @click="logout"
              >
                <v-icon>mdi-logout</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("logout") }}</span>
          </v-tooltip>
          <circle-button
            class="ml-2"
            @click="model = false"
            icon="mdi-close"
          ></circle-button>
        </div>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-6">
        <!-- Section: General Settings -->
        <v-card flat class="mb-6 pa-4" color="#f9f9f9">
          <div class="text-h6 font-weight-bold mb-4 primary--text">
            <v-icon left small color="primary">mdi-tune-variant</v-icon>
            {{ $t("generalSettings") }}
          </div>
          <v-row align="end">
            <v-col cols="12" md="6">
              <v-switch
                dense
                v-model="settings.takeCurrentDate"
                hide-details
                :true-value="true"
                :false-value="false"
                class="mt-0 pt-0"
                :label="$t('current_date')"
              ></v-switch>
              <date-picker
                v-if="!settings.takeCurrentDate"
                single-date
                color="white"
                v-model="date.date"
                :label="$t('date')"
              ></date-picker>
            </v-col>
            <v-col cols="12" md="6">
              <dropdown
                v-model="settings.language"
                :items="languages"
                :label="$t('language')"
                color="white"
                item-value="code"
                item-text="name"
                hide-details
                @change="setLang"
              ></dropdown>
            </v-col>
          </v-row>
        </v-card>

        <!-- Section: Display Mode -->
        <v-card flat class="mb-6 pa-4" color="#f9f9f9">
          <div class="text-h6 font-weight-bold mb-4 primary--text">
            <v-icon left small color="primary">mdi-eye-settings</v-icon>
            {{ $t("displayMode") }}
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-radio-group v-model="settings.mode" hide-details row>
                <v-radio :label="$t('autoMode')" value="auto"></v-radio>
                <v-radio :label="$t('manualMode')" value="manual"></v-radio>
              </v-radio-group>
              <v-expand-transition>
                <div v-if="settings.mode === 'auto'" class="ml-6 mt-2">
                  <text-field
                    :label="$t('autoDuration')"
                    color="white"
                    type="number"
                    :min="2"
                    :suffix="$t('seconds')"
                    :hint="`${$t('nextUserAfter')} ${settings.autoDuration} ${$t(
                      'seconds'
                    )}.`"
                    v-model="settings.autoDuration"
                    persistent-hint
                  ></text-field>
                </div>
                <div v-else class="ml-6 mt-2">
                  <p class="text-caption text-grey-darken-1">
                    {{ $t("nextUserPress") }}
                    <span class="text-h6 font-weight-bold primary--text">{{
                      $t("plus")
                    }}</span
                    >.
                  </p>
                </div>
              </v-expand-transition>
            </v-col>
            <v-col cols="12" md="6">
              <v-radio-group
                v-model="settings.typeTraffic"
                color="error"
                row
                hide-details
              >
                <v-radio :label="$t('trafficNone')" value="none" class="mb-4"></v-radio>
                <v-radio :label="$t('trafficOne')" value="one" class="mb-4"></v-radio>
                <v-radio :label="$t('trafficThree')" value="three"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </v-card>

        <!-- Section: Layout & Appearance -->
        <v-card flat class="mb-6 pa-4" color="#f9f9f9">
          <div class="text-h6 font-weight-bold mb-4 primary--text">
            <v-icon left small color="primary">mdi-format-paint</v-icon>
            {{ $t("appearance") }}
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <dropdown
                :label="$t('usersAndOrders')"
                color="white"
                hide-details
                v-model="settings.colInRow"
                :items="[1, 2, 3, 4, 6]"
                @change="$emit('setPreviousOrders')"
              ></dropdown>
            </v-col>
            <v-col cols="12" md="6">
              <text-field
                color="white"
                :label="$t('clearOrders')"
                hide-details
                v-model="settings.clearPreviousOrdersTime"
                :suffix="$t('seconds')"
              ></text-field>
            </v-col>
            <v-col cols="12" md="6">
              <autocomplete
                :label="$t('userFontSize')"
                hide-details
                color="white"
                v-model="settings.font.size.user"
                :items="sizes"
                @enter="addUserSize"
              ></autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <autocomplete
                :label="$t('foodFontSize')"
                hide-details
                color="white"
                v-model="settings.font.size.food"
                :items="sizes"
                @enter="addFoodSize"
              ></autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch
                v-model="settings.font.uppercase"
                color="error"
                :label="$t('uppercase')"
                hide-details
              ></v-switch>
            </v-col>
            <v-col cols="12" md="6">
              <text-field
                :label="$t('imageSize')"
                hide-details
                v-model="settings.image.size"
                suffix="px"
                color="white"
              ></text-field>
            </v-col>
          </v-row>
        </v-card>

        <!-- Section: Info & Status -->
        <v-card flat class="mb-0 pa-4" color="#f9f9f9">
          <div class="text-h6 font-weight-bold mb-4 primary--text">
            <v-icon left small color="primary">mdi-information-outline</v-icon>
            {{ $t("infoStatus") }}
          </div>
          <v-row>
            <v-col cols="12" md="4">
              <v-switch
                dense
                :label="$t('infoBox')"
                v-model="settings.infoBox"
                hide-details
              ></v-switch>
            </v-col>
            <v-col cols="12" md="4">
              <v-switch
                dense
                :label="$t('statusBox')"
                v-model="settings.statusBox"
                hide-details
              ></v-switch>
            </v-col>
          </v-row>
        </v-card>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="model = false">
          {{ $t("cancel") }}
        </v-btn>
        <primary-button @click="saveSettings">
          <v-icon left>mdi-check</v-icon>
          {{ $t("save") }}
        </primary-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
export default {
  mixins: [hasModel],
  name: "NaplataDialogSettings",
  props: {
    value: Boolean,
    settings: Object,
    config: Object,
    date: {
      type: Object,
      required: true,
    },
  },
  watch: {
    value(val) {
      this.model = val;
    },
    model(val) {
      this.$emit("input", val);
    },
  },
  data() {
    return {
      customUserSizes: [],
      customFoodSizes: [],
    };
  },
  methods: {
    addUserSize(size) {
      const newSize = Number(size?.target?.value);
      if (newSize && !isNaN(newSize)) {
        // Dodaj u listu customUserSizes ako već ne postoji
        if (!this.customUserSizes.includes(newSize)) {
          this.customUserSizes.push(newSize);
        }
        // Postavi kao aktivnu vrednost
        this.settings.font.size.user = newSize;
      }
    },
    addFoodSize(size) {
      const newSize = Number(size?.target?.value);
      if (newSize && !isNaN(newSize)) {
        // Dodaj u listu customFoodSizes ako već ne postoji
        if (!this.customFoodSizes.includes(newSize)) {
          this.customFoodSizes.push(newSize);
        }
        // Postavi kao aktivnu vrednost
        this.settings.font.size.food = newSize;
      }
    },
    setLang() {
      this.$i18n.locale = this.settings.language;
    },
    saveSettings() {
      localStorage.setItem("naplata-settings", JSON.stringify(this.settings));
      this.model = false;
    },
    logout() {
      localStorage.removeItem("restoran-token");
      this.$store.commit("user/LOGOUT", null);
      this.$store.commit("SAVE_RESTAURANT", null);
      this.$store.commit("SAVE_COMPANY", null);
      this.$router.push({ name: "logovanje" });
    },
    formatDate(value) {
      return moment(value).format("DD.MM.YYYY.");
    },
  },
  computed: {
    sizes() {
      // Osnovna lista brojeva od 20 do 50
      const baseNumbers = Array.from({ length: 51 }, (_, i) => i + 20);

      // Dodaj custom brojeve (koji su van opsega 20-50)
      const allCustomNumbers = [
        ...this.customUserSizes,
        ...this.customFoodSizes,
        this.settings.font.size.user,
        this.settings.font.size.food,
      ].filter((s) => s && (s < 20 || s > 70));

      // Kombiniraj sve i ukloni duplikate
      const allNumbers = [...new Set([...baseNumbers, ...allCustomNumbers])];

      // Sortiraj i mapiraj u objekat sa text i value
      return allNumbers
        .sort((a, b) => a - b)
        .map((num) => ({
          text: num + "px",
          value: num,
        }));
    },
    languages() {
      return this.config ? this.config.language : [];
    },
    computedDateFormatted() {
      return this.formatDate(this.date.date);
    },
  },
};
</script>
